import { PublicSeminar, SeminarCalendarEventDto } from '../gql/graphql.ts';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { LecturerCard } from './LecturerCard.tsx';
import { SignalLight } from './SignalLight.tsx';
import {
  getFormattedDate,
  getFormattedSeminarDate,
  getFormattedSeminarDateEvent,
} from './util.ts';
import { restApiUrl } from '../shared/helpers/backendUrl';

interface SidebarItemProps {
  title: string;
  icon: string;
  children?: React.ReactNode;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ title, icon, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1 border-b pb-1">
        <HeroIcon
          name={icon}
          className="mr-1 mt-1 h-6 w-6 shrink-0 text-bts-theme-primary"
        />
        {title}
      </h3>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
};

interface PublicDetailsSidebarProps {
  seminar: Pick<
    PublicSeminar,
    | 'dateText'
    | 'startDate'
    | 'endDate'
    | 'locationText'
    | 'registrationDate'
    | 'lecturers'
    | 'price'
    | 'priceText'
    | 'price2'
    | 'priceText2'
    | 'price3'
    | 'priceText3'
    | 'price4'
    | 'priceText4'
    | 'isAlmostBookedOut'
    | 'isBookedOut'
    | 'id'
    | 'webinarLink'
    | 'webinar'
  >;
  showSignalLight?: boolean;
  showWebinarLink?: boolean;
  calendarEvents?: Pick<SeminarCalendarEventDto, 'begin' | 'end' | 'title'>[];
}

export const PublicDetailsSidebar: React.FC<PublicDetailsSidebarProps> = ({
  seminar,
  showSignalLight,
  showWebinarLink,
  calendarEvents,
}) => {
  // If there is ONLY a priceText, we show the priceText
  // If there is ONLY a numerical value, we show the numerical value (format it correctly and suffix it with "Euro")
  // If there is a priceText AND a numerical value, we show both (format and suffix the numerical value)
  const prices = [
    { value: seminar?.price, text: seminar?.priceText },
    { value: seminar?.price2, text: seminar?.priceText2 },
    { value: seminar?.price3, text: seminar?.priceText3 },
    { value: seminar?.price4, text: seminar?.priceText4 },
  ];

  const pricesLength = prices
    .map((p) => Boolean(p.value || p.text))
    .filter(Boolean).length;

  const amountOfCalendarEvents = 4;
  const amountOfHiddenCalendarEvents = calendarEvents
    ? calendarEvents?.length - amountOfCalendarEvents
    : 0;

  return (
    <aside className="col-span-1 flex flex-col gap-6 md:col-span-3">
      <SidebarLecturersItem lecturers={seminar.lecturers} />
      {(seminar?.dateText || seminar?.startDate || seminar?.endDate) && (
        <SidebarItem icon="Calendar" title="Zeitraum">
          <span>
            {getFormattedSeminarDate(
              seminar.startDate,
              seminar.endDate,
              seminar.dateText,
            )}
            {seminar?.startDate && seminar?.endDate && (
              <a
                href={restApiUrl(`/seminar/${seminar.id}/ics`)}
                className="ml-1 inline-flex items-center hover:text-black"
                target="_blank"
                download=""
                title="Kalender-Datei herunterladen (.ics)"
              >
                <HeroIcon name="ArrowDownTray" className="size-4" />
              </a>
            )}
          </span>
        </SidebarItem>
      )}
      {calendarEvents && calendarEvents.length > 0 && (
        <SidebarItem icon="Clock" title="Termine">
          <ul className="flex w-full flex-col gap-2">
            {calendarEvents.slice(0, amountOfCalendarEvents).map((event, i) => {
              const parsedDates = getFormattedSeminarDateEvent(
                event.begin,
                event.end,
              );
              return (
                <li key={'calendar-event-' + i} className="flex flex-row gap-2">
                  <div className="flex flex-col rounded-md border bg-white px-2 py-0.5">
                    <div className="text-center text-black/40">
                      {parsedDates?.start.monthText?.substring(0, 3)}
                    </div>
                    <div className="text-center text-xl font-semibold">
                      {parsedDates?.start.day}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="text-black/40">{`${parsedDates?.start.hour}:${parsedDates?.start.minute} - ${parsedDates?.end.hour}:${parsedDates?.end.minute} Uhr`}</div>
                    {event.title && <div>{event.title}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
          {calendarEvents.length > amountOfCalendarEvents && (
            <div className="text-sm text-black/40">
              {`und ${amountOfHiddenCalendarEvents > 1 ? `${amountOfHiddenCalendarEvents} weitere Termine` : 'ein weiterer Termin'}`}
            </div>
          )}
        </SidebarItem>
      )}
      {seminar?.locationText && (
        <SidebarItem icon="MapPin" title="Ort">
          {seminar?.locationText}
        </SidebarItem>
      )}

      {pricesLength > 0 && (
        <SidebarItem
          icon="CurrencyEuro"
          title={`Preis${pricesLength > 1 ? 'e' : ''}`}
        >
          <dl className="flex flex-col gap-2">
            {prices?.map(
              (price, i) =>
                (Boolean(price.value) || Boolean(price?.text?.trim())) && (
                  <div key={'price' + (i + 1)}>
                    {price.value ? (
                      <>
                        <dt className="text-lg font-semibold">{`${price.value.toLocaleString('de-DE', { minimumFractionDigits: 2 })} Euro`}</dt>
                        {Boolean(price?.text?.trim()) && (
                          <dd className="text-sm">{`(${price.text?.trim()})`}</dd>
                        )}
                      </>
                    ) : (
                      <dt className="text-lg font-semibold">{`${price.text?.trim()}`}</dt>
                    )}
                  </div>
                ),
            )}
          </dl>
        </SidebarItem>
      )}
      {showWebinarLink && (seminar.webinarLink || seminar.webinar) && (
        <SidebarItem icon="VideoCamera" title="Online-Link">
          {seminar.webinarLink ? (
            <a
              href={seminar.webinarLink}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-bts-theme-primary hover:underline"
            >
              {seminar.webinarLink.replace(/^https?:\/\//, '')}
            </a>
          ) : (
            <span>Bitte kontaktiere den Veranstalter.</span>
          )}
        </SidebarItem>
      )}
      {(showSignalLight || seminar.registrationDate) && (
        <SidebarItem icon="Bell" title="Anmeldestatus">
          <div className="flex flex-col gap-6">
            {seminar.registrationDate && (
              <div>
                Anmeldeschluss ist der
                <br />
                {getFormattedDate(seminar.registrationDate)}
              </div>
            )}
            {showSignalLight && (
              <SignalLight
                isBookedOut={seminar.isBookedOut}
                isAlmostBookedOut={seminar.isAlmostBookedOut}
                registrationDate={seminar.registrationDate}
                withText
              />
            )}
          </div>
        </SidebarItem>
      )}
    </aside>
  );
};

interface SidebarLecturersItemProps {
  lecturers: PublicSeminar['lecturers'];
}

const SidebarLecturersItem: React.FC<SidebarLecturersItemProps> = ({
  lecturers,
}) => {
  return (
    lecturers &&
    lecturers?.length > 0 && (
      <SidebarItem icon="UserGroup" title={'Referierende'}>
        <ul className="flex w-full list-inside list-disc flex-col items-center gap-2">
          {lecturers?.map((lecturer, i) => (
            <LecturerCard
              key={'lecturer' + i}
              image={lecturer?.pathToProfileImage as string}
              title={lecturer?.name as string}
              subtitle={lecturer?.jobTitle as string}
              vita={lecturer?.vita as string}
            />
          ))}
        </ul>
      </SidebarItem>
    )
  );
};
